import React, { FC, useState, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import styled from 'styled-components';
import { TextInputField } from '../Forms/fields';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { useDispatch } from 'react-redux';
import { alertSuccess, alertError } from '../../store/alerts/actions';
import { getAuthenticatorSetupAsync, confirmAuthenticatorSetupAsync } from '../../ajax/requests';
import AppButton from '../AppButton';

const SetupContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacer-M);
`;

const Description = styled.p`
    color: var(--text-muted);
    font-size: 0.9rem;
    text-align: center;
`;

const QrCodeContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacer-S);
`;

const QrCodeImage = styled.img`
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: var(--spacer-XS);
    padding: var(--spacer-S);
`;

const SecretKey = styled.code`
    background-color: var(--bg-light);
    padding: var(--spacer-XS) var(--spacer-S);
    border-radius: var(--spacer-XS);
    font-size: 0.85rem;
    letter-spacing: 0.1rem;
    word-break: break-all;
    text-align: center;
    user-select: all;
`;

const Label = styled.span`
    color: var(--text-muted);
    font-size: 0.85rem;
`;

export interface AuthenticatorSetupModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSetupComplete: () => void;
}

export const AuthenticatorSetupModal: FC<AuthenticatorSetupModalProps> = ({
    isOpen,
    onClose,
    onSetupComplete,
}) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [setupData, setSetupData] = useState<{ secret: string; otpAuthUri: string; sessionId: string } | null>(null);
    const [code, setCode] = useState('');

    const loadSetup = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getAuthenticatorSetupAsync();
            setSetupData(data);
        } catch {
            dispatch(alertError(t(TK.somethingWentWrong)));
            onClose();
        } finally {
            setLoading(false);
        }
    }, [dispatch, t, onClose]);

    React.useEffect(() => {
        if (isOpen && !setupData) {
            loadSetup();
        }
        if (!isOpen) {
            setSetupData(null);
            setCode('');
        }
    }, [isOpen, setupData, loadSetup]);

    const handleConfirm = useCallback(async () => {
        if (!setupData || code.length !== 6) return;
        setConfirming(true);
        try {
            await confirmAuthenticatorSetupAsync(setupData.sessionId, code);
            dispatch(alertSuccess(t(TK.authenticatorSetupSuccess)));
            onSetupComplete();
            onClose();
        } catch (error: any) {
            const msg = error?.data?.message || error?.response?.data?.message || t(TK.invalidOrExpiredCode);
            dispatch(alertError(msg));
        } finally {
            setConfirming(false);
        }
    }, [setupData, code, dispatch, t, onSetupComplete, onClose]);

    const handleKeyUp = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && code.length === 6 && !confirming) {
            handleConfirm();
        }
    }, [code, confirming, handleConfirm]);

    const handleClose = useCallback(() => {
        setSetupData(null);
        setCode('');
        onClose();
    }, [onClose]);

    const qrCodeUrl = setupData?.otpAuthUri
        ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.otpAuthUri)}`
        : '';

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered size="md">
            <ModalHeader toggle={handleClose}>
                {t(TK.authenticatorSetup)}
            </ModalHeader>
            <ModalBody>
                {loading ? (
                    <SetupContainer>
                        <Spinner />
                    </SetupContainer>
                ) : setupData ? (
                    <SetupContainer>
                        <Description>{t(TK.authenticatorSetupDescription)}</Description>
                        <QrCodeContainer>
                            <Label>{t(TK.scanQrCode)}</Label>
                            <QrCodeImage
                                src={qrCodeUrl}
                                alt="QR Code"
                                width={200}
                                height={200}
                            />
                            <Label>{t(TK.orEnterManually)}</Label>
                            <SecretKey>{setupData.secret}</SecretKey>
                        </QrCodeContainer>
                        <TextInputField
                            id="authenticator-code"
                            type="text"
                            placeholder="000000"
                            value={code}
                            onChange={setCode}
                            onKeyUp={handleKeyUp}
                            maxLength={6}
                            inputStyle={{ fontSize: '2rem', textAlign: 'center', letterSpacing: '0.5rem' }}
                            style={{ maxWidth: '12rem', alignSelf: 'center' }}
                            autoFocus
                        />
                    </SetupContainer>
                ) : null}
            </ModalBody>
            <ModalFooter>
                <AppButton color="secondary" onClick={handleClose} disabled={confirming}>
                    {t(TK.cancel)}
                </AppButton>
                <AppButton
                    color="success"
                    onClick={handleConfirm}
                    disabled={code.length !== 6 || confirming || !setupData}
                >
                    {confirming && <Spinner size="sm" style={{ marginRight: '0.5rem' }} />}
                    {t(TK.confirmSetup)}
                </AppButton>
            </ModalFooter>
        </Modal>
    );
};

export default AuthenticatorSetupModal;
